import React, { useState, useEffect } from 'react';
import {
    Dialog, Typography, Button, Radio, RadioGroup, FormControlLabel,
    TextField, LinearProgress, Box, IconButton, Fade, Rating,
    Checkbox, FormGroup, Slide, Paper, Grid
} from '@mui/material';
import {
    Close, NavigateNext, ArrowBack, CheckCircleOutline,
    Star, Favorite, SentimentVeryDissatisfied, SentimentSatisfied, SentimentVerySatisfied
} from '@mui/icons-material';
import { ref, get, push } from "firebase/database";
import { database } from '../adm/admin/firebaseConfig';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function PesquisaModal() {
    const [open, setOpen] = useState(false);
    const [survey, setSurvey] = useState(null);
    const [answers, setAnswers] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [submitted, setSubmitted] = useState(false);

    // Estado para campos de contato
    const [contactData, setContactData] = useState({ nome: '', email: '', telefone: '' });

    useEffect(() => {
        const initSurvey = async () => {
            try {
                const snap = await get(ref(database, 'pesquisas_satisfacao'));
                if (snap.exists()) {
                    const data = snap.val();
                    const activeEntry = Object.entries(data).find(([id, s]) => s.active);

                    if (activeEntry && !localStorage.getItem(`respondeu_pesquisa_${activeEntry[0]}`)) {
                        setSurvey({ id: activeEntry[0], ...activeEntry[1] });
                        setTimeout(() => setOpen(true), 1800);
                    }
                }
            } catch (e) {
                console.error("Erro ao carregar pesquisa pública:", e);
            }
        };
        initSurvey();
    }, []);

    const handleAnswerChange = (pageIndex, val) => {
        setAnswers(prev => ({ ...prev, [pageIndex]: val }));
    };

    const handleCheckboxChange = (pageIndex, option) => {
        const currentArr = answers[pageIndex] || [];
        if (currentArr.includes(option)) {
            setAnswers(prev => ({ ...prev, [pageIndex]: currentArr.filter(item => item !== option) }));
        } else {
            setAnswers(prev => ({ ...prev, [pageIndex]: [...currentArr, option] }));
        }
    };

    const handleSend = async () => {
        if (!survey) return;

        // Se a página atual for formulário de contato, salvar os dados
        const finalAnswers = { ...answers };
        if (survey.questions[currentPage]?.type === 'contact') {
            finalAnswers[currentPage] = contactData;
        }

        try {
            await push(ref(database, `respostas_pesquisas/${survey.id}`), {
                respostas: finalAnswers,
                timestamp: Date.now()
            });
            localStorage.setItem(`respondeu_pesquisa_${survey.id}`, 'true');
            setSubmitted(true);
            setTimeout(() => {
                setOpen(false);
            }, 3000);
        } catch (e) {
            console.error(e);
        }
    };

    const handleIgnore = () => {
        if (!survey) return;
        localStorage.setItem(`respondeu_pesquisa_${survey.id}`, 'true');
        setOpen(false);
    };

    if (!survey) return null;

    const themeColor = survey.themeColor || '#0F71FD';
    const isDrawer = survey.displayMode === 'popup_bottom_right';
    const currentQ = survey.questions[currentPage];
    const progress = ((currentPage + 1) / survey.questions.length) * 100;

    const isCurrentAnswerValid = () => {
        if (!currentQ.required) return true;
        const ans = answers[currentPage];

        if (currentQ.type === 'contact') {
            return contactData.nome.trim() !== '' && (contactData.email.trim() !== '' || contactData.telefone.trim() !== '');
        }
        if (currentQ.type === 'checkbox') {
            return Array.isArray(ans) && ans.length > 0;
        }
        return ans !== undefined && ans !== '';
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={() => { }}
            hideBackdrop={isDrawer} // Sem escurecimento de fundo se for o popup do canto
            PaperProps={{
                sx: {
                    position: isDrawer ? 'fixed' : 'relative',
                    bottom: isDrawer ? { xs: 10, sm: 24 } : 'auto',
                    right: isDrawer ? { xs: 10, sm: 24 } : 'auto',
                    m: isDrawer ? 0 : 2,
                    bgcolor: 'rgba(18, 18, 20, 0.96)',
                    backdropFilter: 'blur(16px)',
                    color: '#FFF',
                    borderRadius: '20px',
                    width: { xs: 'calc(100% - 20px)', sm: '440px' },
                    border: '1px solid rgba(255,255,255,0.12)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
                    overflow: 'hidden'
                }
            }}
        >
            {/* TELA DE AGRADECIMENTO AO CONCLUIR */}
            {submitted ? (
                <Box p={5} textAlign="center" display="flex" flexDirection="column" alignItems="center">
                    <CheckCircleOutline sx={{ fontSize: 70, color: '#10B981', mb: 2 }} />
                    <Typography variant="h6" fontWeight="800" color="#FFF" mb={1}>
                        Obrigado!
                    </Typography>
                    <Typography variant="body2" color="#A1A1AA">
                        {survey.thankYouMessage || "Sua participação é de extrema importância para nós."}
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* BARRA DE TOPO */}
                    <Box p={2.5} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: themeColor }} />
                            <Typography variant="caption" sx={{ color: '#A1A1AA', fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>
                                PASSO {currentPage + 1} DE {survey.questions.length}
                            </Typography>
                        </Box>
                        <IconButton onClick={handleIgnore} size="small" sx={{ color: '#71717A', '&:hover': { color: '#FFF' } }}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 3,
                            bgcolor: '#27272A',
                            '& .MuiLinearProgress-bar': { bgcolor: themeColor, transition: 'width 0.4s ease' }
                        }}
                    />

                    {/* CORPO DA PERGUNTA */}
                    <Box p={3} pt={3} minHeight="220px">
                        <Fade in={true} key={currentPage}>
                            <Box>
                                <Typography variant="h6" sx={{ mb: 2.5, fontWeight: '700', fontSize: '1.05rem', lineHeight: 1.4 }}>
                                    {currentQ.question}
                                </Typography>

                                {/* RATING / ESTRELAS (1 a 5) */}
                                {currentQ.type === 'rating' && (
                                    <Box display="flex" flexDirection="column" alignItems="center" my={2}>
                                        <Rating
                                            size="large"
                                            value={Number(answers[currentPage]) || 0}
                                            onChange={(e, newValue) => {
                                                handleAnswerChange(currentPage, newValue);
                                                if (currentPage < survey.questions.length - 1) {
                                                    setTimeout(() => setCurrentPage(currentPage + 1), 300);
                                                }
                                            }}
                                            sx={{
                                                fontSize: '2.8rem',
                                                '& .MuiRating-iconFilled': { color: '#F59E0B' },
                                                '& .MuiRating-iconEmpty': { color: '#3F3F46' }
                                            }}
                                        />
                                    </Box>
                                )}

                                {/* ESCALA NPS (0 a 10) */}
                                {currentQ.type === 'nps' && (
                                    <Box my={2}>
                                        <Grid container spacing={0.5}>
                                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
                                                const isSelected = answers[currentPage] === num;
                                                return (
                                                    <Grid item xs={2.18} key={num} sx={{ mb: 0.5 }}>
                                                        <Button
                                                            fullWidth
                                                            variant={isSelected ? "contained" : "outlined"}
                                                            onClick={() => {
                                                                handleAnswerChange(currentPage, num);
                                                                if (currentPage < survey.questions.length - 1) {
                                                                    setTimeout(() => setCurrentPage(currentPage + 1), 300);
                                                                }
                                                            }}
                                                            sx={{
                                                                minWidth: 0,
                                                                p: 0.8,
                                                                fontWeight: 'bold',
                                                                bgcolor: isSelected ? themeColor : '#18181B',
                                                                color: isSelected ? '#FFF' : '#A1A1AA',
                                                                borderColor: isSelected ? themeColor : '#27272A',
                                                                '&:hover': { bgcolor: themeColor, color: '#FFF' }
                                                            }}
                                                        >
                                                            {num}
                                                        </Button>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                        <Box display="flex" justifyContent="space-between" mt={1}>
                                            <Typography variant="caption" color="#71717A">Pouco Provável</Typography>
                                            <Typography variant="caption" color="#71717A">Muito Provável</Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* SELEÇÃO ÚNICA (RADIO) */}
                                {currentQ.type === 'radio' && (
                                    <RadioGroup value={answers[currentPage] || ''} onChange={(e) => handleAnswerChange(currentPage, e.target.value)}>
                                        {(currentQ.options || "").split(',').map((opt, i) => (
                                            <FormControlLabel
                                                key={i}
                                                value={opt.trim()}
                                                control={<Radio sx={{ color: '#52525B', '&.Mui-checked': { color: themeColor } }} />}
                                                label={opt.trim()}
                                                sx={{
                                                    mb: 1,
                                                    bgcolor: '#18181B',
                                                    border: '1px solid #27272A',
                                                    borderRadius: '10px',
                                                    ml: 0, mr: 0, pr: 2,
                                                    '&:hover': { borderColor: '#3F3F46' }
                                                }}
                                            />
                                        ))}
                                    </RadioGroup>
                                )}

                                {/* MÚLTIPLA SELEÇÃO (CHECKBOX) */}
                                {currentQ.type === 'checkbox' && (
                                    <FormGroup>
                                        {(currentQ.options || "").split(',').map((opt, i) => {
                                            const optionTrimmed = opt.trim();
                                            const isChecked = (answers[currentPage] || []).includes(optionTrimmed);
                                            return (
                                                <FormControlLabel
                                                    key={i}
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={() => handleCheckboxChange(currentPage, optionTrimmed)}
                                                            sx={{ color: '#52525B', '&.Mui-checked': { color: themeColor } }}
                                                        />
                                                    }
                                                    label={optionTrimmed}
                                                    sx={{
                                                        mb: 1,
                                                        bgcolor: '#18181B',
                                                        border: '1px solid #27272A',
                                                        borderRadius: '10px',
                                                        ml: 0, mr: 0, pr: 2
                                                    }}
                                                />
                                            );
                                        })}
                                    </FormGroup>
                                )}

                                {/* TEXTO LIVRE */}
                                {currentQ.type === 'text' && (
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder="Digite sua resposta aqui..."
                                        value={answers[currentPage] || ''}
                                        onChange={(e) => handleAnswerChange(currentPage, e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                bgcolor: '#18181B',
                                                color: '#FFF',
                                                borderRadius: '10px',
                                                '& fieldset': { borderColor: '#27272A' },
                                                '&:hover fieldset': { borderColor: themeColor }
                                            }
                                        }}
                                    />
                                )}

                                {/* FORMULÁRIO DE CONTATO */}
                                {currentQ.type === 'contact' && (
                                    <Box display="flex" flexDirection="column" gap={1.5}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Seu Nome Completo"
                                            value={contactData.nome}
                                            onChange={(e) => setContactData({ ...contactData, nome: e.target.value })}
                                            sx={{ bgcolor: '#18181B', borderRadius: '8px', input: { color: '#FFF' }, label: { color: '#A1A1AA' } }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Seu E-mail"
                                            value={contactData.email}
                                            onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                                            sx={{ bgcolor: '#18181B', borderRadius: '8px', input: { color: '#FFF' }, label: { color: '#A1A1AA' } }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="WhatsApp / Telefone"
                                            value={contactData.telefone}
                                            onChange={(e) => setContactData({ ...contactData, telefone: e.target.value })}
                                            sx={{ bgcolor: '#18181B', borderRadius: '8px', input: { color: '#FFF' }, label: { color: '#A1A1AA' } }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Fade>
                    </Box>

                    {/* BOTÕES DE NAVEGAÇÃO E IGNORAR */}
                    <Box p={2.5} pt={0} display="flex" flexDirection="column" gap={1}>
                        <Box display="flex" gap={1}>
                            {currentPage > 0 && (
                                <Button
                                    onClick={() => setCurrentPage(currentPage - 1)}
                                    sx={{ color: '#A1A1AA', textTransform: 'none', fontWeight: 'bold' }}
                                    startIcon={<ArrowBack />}
                                >
                                    Voltar
                                </Button>
                            )}

                            {currentPage < survey.questions.length - 1 ? (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setCurrentPage(currentPage + 1)}
                                    disabled={!isCurrentAnswerValid()}
                                    sx={{
                                        bgcolor: themeColor,
                                        borderRadius: '10px',
                                        py: 1.2,
                                        fontWeight: '800',
                                        '&:hover': { bgcolor: themeColor }
                                    }}
                                >
                                    Avançar <NavigateNext />
                                </Button>
                            ) : (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSend}
                                    disabled={!isCurrentAnswerValid()}
                                    sx={{
                                        bgcolor: '#10B981',
                                        borderRadius: '10px',
                                        py: 1.2,
                                        fontWeight: '800',
                                        '&:hover': { bgcolor: '#059669' }
                                    }}
                                >
                                    Concluir e Enviar <CheckCircleOutline sx={{ ml: 1, fontSize: 18 }} />
                                </Button>
                            )}
                        </Box>

                        <Button
                            onClick={handleIgnore}
                            sx={{
                                color: '#71717A',
                                fontSize: '0.7rem',
                                textTransform: 'none',
                                '&:hover': { color: '#EF4444' }
                            }}
                        >
                            Não tenho interesse em responder
                        </Button>
                    </Box>

                    {/* ASSINATURA */}
                    <Box textAlign="center" pb={1.5}>
                        <Typography variant="caption" sx={{ color: '#52525B', fontSize: '10px', fontWeight: 700, letterSpacing: 0.5 }}>
                            POWERED BY BIGSTAR SOFTWARES
                        </Typography>
                    </Box>
                </>
            )}
        </Dialog>
    );
}